import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Account } from '@/features/transactions/types'
import { fetchAccountTransactions, finishReconcile, summarizeForReconcile } from './reconcile'

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

export function ReconcileDialog({ account, onClose }: { account: Account; onClose: () => void }) {
  const qc = useQueryClient()
  const txns = useQuery({
    queryKey: ['transactions', 'account', account.id] as const,
    queryFn: () => fetchAccountTransactions(account.id),
    staleTime: 0,
  })
  const summary = txns.data ? summarizeForReconcile(txns.data) : null
  const [input, setInput] = useState('')
  const statementBalance = input.trim() === '' ? null : Number(input)
  const diff =
    summary && statementBalance !== null && Number.isFinite(statementBalance)
      ? Math.round((statementBalance - summary.clearedBalance) * 100) / 100
      : null

  const finish = useMutation({
    mutationFn: () => finishReconcile(account, statementBalance!, summary!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['transactions'] })
      void qc.invalidateQueries({ queryKey: ['accounts'] })
      onClose()
    },
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-labelledby="reconcile-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm space-y-4 rounded-xl border border-zinc-200 bg-white p-5 shadow-2xl dark:border-zinc-800 dark:bg-zinc-950"
      >
        <h2 id="reconcile-title" className="text-lg font-semibold">
          Reconcile {account.name}
        </h2>

        {txns.isLoading && <p className="text-sm text-zinc-500">Loading transactions…</p>}

        {summary && (
          <>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-500">Cleared balance in app</span>
                <span className="tabular-nums font-medium">{usd.format(summary.clearedBalance)}</span>
              </div>
              <div className="flex justify-between text-zinc-500">
                <span>Uncleared (pending)</span>
                <span className="tabular-nums">{usd.format(summary.unclearedBalance)}</span>
              </div>
            </div>

            <label className="block text-sm">
              <span className="mb-1 block text-zinc-500">Bank statement balance</span>
              <input
                type="number"
                step="0.01"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={summary.clearedBalance.toFixed(2)}
                autoFocus
                className="w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 tabular-nums dark:border-zinc-700 dark:bg-zinc-900"
              />
            </label>

            {diff !== null && (
              <p className={`text-sm ${diff === 0 ? 'text-funded' : 'text-amber-600 dark:text-amber-500'}`}>
                {diff === 0
                  ? 'Balances match — ready to lock.'
                  : `Off by ${usd.format(diff)} — finishing creates a reconciliation adjustment.`}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={diff === null || finish.isPending}
                onClick={() => finish.mutate()}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {finish.isPending
                  ? 'Reconciling…'
                  : `Finish · lock ${summary.toLockIds.length} cleared`}
              </button>
            </div>
            {finish.isError && (
              <p role="alert" className="text-sm text-overspent">
                {(finish.error as Error).message}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
